<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import { useDeviceStore } from "@/entities/device/model";
import { useApiRegisterDevice, useEvaluationStore } from "@/entities/evaluation/model";
import { useCaptchaStore } from "@/features/captcha/model";
import { CaptchaWidget } from "@/features/captcha/ui";
import { PARTNERS_DOMAINS, useWidgetContext } from "@/shared/config";
import { logger } from "@/shared/lib";
import { useGlobalStore } from "@/shared/model";
import {
    UiButton,
    UiField,
    UiNotification,
    UiSelect,
    UiStepLayout,
    startViewTransition,
} from "@/shared/ui";

const { t } = useI18n({ useScope: "global" });
const { settings } = useWidgetContext();
const deviceStore = useDeviceStore();
const captchaStore = useCaptchaStore();
const evaluationStore = useEvaluationStore();
const globalStore = useGlobalStore();
const { mutateAsync: registerDevice, isLoading: isRegistering } = useApiRegisterDevice();

const isTradeInWording = computed(
    () =>
        settings.domain === PARTNERS_DOMAINS.AVO_AFRICA ||
        settings.domain === PARTNERS_DOMAINS.TAKEALOT,
);

const title = computed(() =>
    t(isTradeInWording.value ? "chooseDevice.titleTradeIn" : "chooseDevice.title"),
);

const subtitle = computed(() =>
    t(
        settings.domain === PARTNERS_DOMAINS.TAKEALOT
            ? "chooseDevice.subtitleTradeIn"
            : "chooseDevice.subtitle",
    ),
);

const canContinue = computed(
    () => deviceStore.isComplete && captchaStore.isPassed && !isRegistering.value,
);

const isDeviceNotAccepted = ref(false);

watch(
    () => deviceStore.chain.join(),
    () => (isDeviceNotAccepted.value = false),
);

async function continueToNextStep(): Promise<void> {
    if (!canContinue.value) return;

    // A failed request must not leave the previous registration in place
    evaluationStore.reset();

    try {
        await registerDevice(deviceStore.productId);
    } catch (error) {
        logger.error("device is not registered", error);
    }

    if (!evaluationStore.isAccepted) {
        await startViewTransition(() => (isDeviceNotAccepted.value = true));
        return;
    }

    await globalStore.switchStep("howYouRateDevice");
}
</script>

<template>
    <UiStepLayout>
        <template #header>
            <h2 class="title">{{ title }}</h2>
            <p class="subtitle">{{ subtitle }}</p>
        </template>

        <form class="form" @submit.prevent="continueToNextStep">
            <UiField>
                <template #hintTop>{{ t("chooseDevice.brand") }}</template>
                <UiSelect
                    v-model="deviceStore.brand"
                    :options="deviceStore.brandOptions"
                    label="name"
                    :disabled="isRegistering"
                />
            </UiField>

            <UiField>
                <template #hintTop>{{ t("chooseDevice.category") }}</template>
                <UiSelect
                    v-model="deviceStore.category"
                    :options="deviceStore.categoryOptions"
                    label="name"
                    :disabled="isRegistering || !deviceStore.brand"
                />
            </UiField>

            <UiField>
                <template #hintTop>{{ t("chooseDevice.model") }}</template>
                <UiSelect
                    v-model="deviceStore.model"
                    :options="deviceStore.modelOptions"
                    label="name"
                    :is-loading="deviceStore.isModelsLoading"
                    :disabled="isRegistering || !deviceStore.category"
                />
            </UiField>

            <template v-if="deviceStore.isCommercialNotebook">
                <UiField>
                    <template #hintTop>{{ t("chooseDevice.cpu") }}</template>
                    <UiSelect
                        v-model="deviceStore.cpu"
                        :options="deviceStore.cpuOptions"
                        label="keyShow2"
                        :is-loading="deviceStore.isProductsLoading"
                        :disabled="isRegistering || !deviceStore.model"
                    />
                </UiField>

                <UiField>
                    <template #hintTop>{{ t("chooseDevice.gpu") }}</template>
                    <UiSelect
                        v-model="deviceStore.gpu"
                        :options="deviceStore.gpuOptions"
                        label="keyShow3"
                        :disabled="isRegistering || !deviceStore.cpu"
                    />
                </UiField>

                <UiField>
                    <template #hintTop>{{ t("chooseDevice.ram") }}</template>
                    <UiSelect
                        v-model="deviceStore.ram"
                        :options="deviceStore.ramOptions"
                        label="keyShow4"
                        :disabled="isRegistering || !deviceStore.gpu"
                    />
                </UiField>

                <UiField>
                    <template #hintTop>{{ t("chooseDevice.memory") }}</template>
                    <UiSelect
                        v-model="deviceStore.memory"
                        :options="deviceStore.memoryOptions"
                        label="keyShow5"
                        :disabled="isRegistering || !deviceStore.ram"
                    />
                </UiField>
            </template>

            <template v-else>
                <UiField>
                    <template #hintTop>{{ t("chooseDevice.other") }}</template>
                    <UiSelect
                        v-model="deviceStore.storage"
                        :options="deviceStore.storageOptions"
                        label="storage"
                        :is-loading="deviceStore.isProductsLoading"
                        :disabled="isRegistering || !deviceStore.model"
                    />
                </UiField>

                <UiField>
                    <template #hintTop>{{ t("chooseDevice.color") }}</template>
                    <UiSelect
                        v-model="deviceStore.color"
                        :options="deviceStore.colorOptions"
                        label="color"
                        :disabled="isRegistering || !deviceStore.storage"
                    />
                </UiField>
            </template>

            <UiNotification v-if="isDeviceNotAccepted" type="danger" class="not-accepted">
                {{ t("chooseDevice.deviceNotAccepted") }}
            </UiNotification>

            <CaptchaWidget />

            <UiButton type="submit" class="next" :loading="isRegistering" :disabled="!canContinue">
                {{ t("common.continue") }}
            </UiButton>
        </form>
    </UiStepLayout>
</template>

<style scoped>
.title {
    margin: 0;
    padding-bottom: 8px;
    font-size: calc(var(--custom-font-size) * 1.429);
    font-weight: 700;
    line-height: 1;
}

.subtitle {
    margin: 0;
    font-size: var(--custom-font-size);
    line-height: 1.429;
    filter: grayscale(0.65);
}

.form {
    display: flex;
    flex-direction: column;
}

.not-accepted {
    margin-top: 4px;
    margin-bottom: 12px;
}

.next {
    margin-top: 12px;
    margin-left: auto;
}
</style>
